// frontend/src/features/equipment/EquipmentSetupWizard/WizardContext.tsx
import React, { createContext, useContext, useReducer, useCallback, useMemo } from 'react';
import type { PendingEquipment, WizardState, WizardStep } from './types';
import { WIZARD_STEPS, INITIAL_WIZARD_STATE } from './types';

// Action types
type WizardAction =
  | { type: 'OPEN_WIZARD' }
  | { type: 'CLOSE_WIZARD' }
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'GO_TO_STEP'; step: number }
  | { type: 'ADD_EQUIPMENT'; equipment: PendingEquipment }
  | { type: 'UPDATE_EQUIPMENT'; tempId: string; updates: Partial<PendingEquipment> }
  | { type: 'REMOVE_EQUIPMENT'; tempId: string }
  | { type: 'REMOVE_PENDING_BY_TEMP_IDS'; tempIds: string[] }
  | { type: 'SET_EQUIPMENT'; equipment: PendingEquipment[] }
  | { type: 'SET_PREEXISTING_CATEGORIES'; categories: string[] }
  | { type: 'MARK_CATEGORY_SAVED'; categoryId: string }
  | { type: 'ADD_SESSION_SAVED_EQUIPMENT'; equipment: PendingEquipment[] }
  | { type: 'SKIP_CATEGORY'; categoryId: string }
  | { type: 'UNSKIP_CATEGORY'; categoryId: string }
  | { type: 'RESET_WIZARD' };

// Context value type
interface WizardContextValue {
  state: WizardState;
  openWizard: () => void;
  closeWizard: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  addEquipment: (equipment: PendingEquipment) => void;
  updateEquipment: (tempId: string, updates: Partial<PendingEquipment>) => void;
  removeEquipment: (tempId: string) => void;
  removePendingByTempIds: (tempIds: string[]) => void;
  setEquipment: (equipment: PendingEquipment[]) => void;
  setPreexistingCategories: (categories: string[]) => void;
  markCategorySaved: (categoryId: string) => void;
  addSessionSavedEquipment: (equipment: PendingEquipment[]) => void;
  skipCategory: (categoryId: string) => void;
  unskipCategory: (categoryId: string) => void;
  resetWizard: () => void;
  // Computed values
  isFirstStep: boolean;
  isLastStep: boolean;
  currentStepInfo: WizardStep;
  totalEquipmentCount: number;
}

const MAX_STEP = WIZARD_STEPS.length - 1;

// Reducer
function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'OPEN_WIZARD':
      return {
        ...INITIAL_WIZARD_STATE,
        isOpen: true,
      };

    case 'CLOSE_WIZARD':
      return {
        ...state,
        isOpen: false,
      };

    case 'NEXT_STEP':
      return {
        ...state,
        currentStep: Math.min(state.currentStep + 1, MAX_STEP),
      };

    case 'PREV_STEP':
      return {
        ...state,
        currentStep: Math.max(state.currentStep - 1, 0),
      };

    case 'GO_TO_STEP':
      return {
        ...state,
        currentStep: Math.max(0, Math.min(action.step, MAX_STEP)),
      };

    case 'ADD_EQUIPMENT':
      return {
        ...state,
        pendingEquipment: [...state.pendingEquipment, action.equipment],
      };

    case 'UPDATE_EQUIPMENT':
      return {
        ...state,
        pendingEquipment: state.pendingEquipment.map((eq) =>
          eq.tempId === action.tempId ? { ...eq, ...action.updates } : eq
        ),
      };

    case 'REMOVE_EQUIPMENT':
      return {
        ...state,
        pendingEquipment: state.pendingEquipment.filter(
          (eq) => eq.tempId !== action.tempId
        ),
      };

    case 'REMOVE_PENDING_BY_TEMP_IDS':
      return {
        ...state,
        pendingEquipment: state.pendingEquipment.filter(
          (eq) => !action.tempIds.includes(eq.tempId)
        ),
      };

    case 'SET_EQUIPMENT':
      return {
        ...state,
        pendingEquipment: action.equipment,
      };

    case 'SET_PREEXISTING_CATEGORIES':
      return {
        ...state,
        preexistingCategories: action.categories,
      };

    case 'MARK_CATEGORY_SAVED':
      if (state.savedCategories.includes(action.categoryId)) {
        return state;
      }
      return {
        ...state,
        savedCategories: [...state.savedCategories, action.categoryId],
      };

    case 'ADD_SESSION_SAVED_EQUIPMENT':
      return {
        ...state,
        sessionSavedEquipment: [
          ...state.sessionSavedEquipment,
          ...action.equipment,
        ],
      };

    case 'SKIP_CATEGORY':
      if (state.skippedCategories.includes(action.categoryId)) {
        return state;
      }
      return {
        ...state,
        skippedCategories: [...state.skippedCategories, action.categoryId],
      };

    case 'UNSKIP_CATEGORY':
      return {
        ...state,
        skippedCategories: state.skippedCategories.filter(
          (id) => id !== action.categoryId
        ),
      };

    case 'RESET_WIZARD':
      return {
        ...INITIAL_WIZARD_STATE,
        isOpen: true,
      };

    default:
      return state;
  }
}

// Context
const WizardContext = createContext<WizardContextValue | undefined>(undefined);

// Provider component
export function WizardProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(wizardReducer, INITIAL_WIZARD_STATE);

  const openWizard = useCallback(() => {
    dispatch({ type: 'OPEN_WIZARD' });
  }, []);

  const closeWizard = useCallback(() => {
    dispatch({ type: 'CLOSE_WIZARD' });
  }, []);

  const nextStep = useCallback(() => {
    dispatch({ type: 'NEXT_STEP' });
  }, []);

  const prevStep = useCallback(() => {
    dispatch({ type: 'PREV_STEP' });
  }, []);

  const goToStep = useCallback((step: number) => {
    dispatch({ type: 'GO_TO_STEP', step });
  }, []);

  const addEquipment = useCallback((equipment: PendingEquipment) => {
    dispatch({ type: 'ADD_EQUIPMENT', equipment });
  }, []);

  const updateEquipment = useCallback(
    (tempId: string, updates: Partial<PendingEquipment>) => {
      dispatch({ type: 'UPDATE_EQUIPMENT', tempId, updates });
    },
    []
  );

  const removeEquipment = useCallback((tempId: string) => {
    dispatch({ type: 'REMOVE_EQUIPMENT', tempId });
  }, []);

  const removePendingByTempIds = useCallback((tempIds: string[]) => {
    dispatch({ type: 'REMOVE_PENDING_BY_TEMP_IDS', tempIds });
  }, []);

  const setEquipment = useCallback((equipment: PendingEquipment[]) => {
    dispatch({ type: 'SET_EQUIPMENT', equipment });
  }, []);

  const setPreexistingCategories = useCallback((categories: string[]) => {
    dispatch({ type: 'SET_PREEXISTING_CATEGORIES', categories });
  }, []);

  const markCategorySaved = useCallback((categoryId: string) => {
    dispatch({ type: 'MARK_CATEGORY_SAVED', categoryId });
  }, []);

  const addSessionSavedEquipment = useCallback(
    (equipment: PendingEquipment[]) => {
      dispatch({ type: 'ADD_SESSION_SAVED_EQUIPMENT', equipment });
    },
    []
  );

  const skipCategory = useCallback((categoryId: string) => {
    dispatch({ type: 'SKIP_CATEGORY', categoryId });
  }, []);

  const unskipCategory = useCallback((categoryId: string) => {
    dispatch({ type: 'UNSKIP_CATEGORY', categoryId });
  }, []);

  const resetWizard = useCallback(() => {
    dispatch({ type: 'RESET_WIZARD' });
  }, []);

  // Computed values
  const isFirstStep = state.currentStep === 0;
  const isLastStep = state.currentStep === MAX_STEP;
  // currentStep is clamped in reducer, but add defensive fallback for safety
  // WIZARD_STEPS is a constant with 7 entries; index 0 is always defined
  const currentStepInfo = WIZARD_STEPS[state.currentStep] ?? WIZARD_STEPS[0] as WizardStep;
  const totalEquipmentCount = state.pendingEquipment.reduce(
    (sum, eq) => sum + eq.quantity,
    0
  );

  // Note: Action functions (openWizard, closeWizard, etc.) are stable due to useCallback
  // with empty deps, so we only need to track state-derived values in the dependency array
  const value = useMemo<WizardContextValue>(
    () => ({
      state,
      openWizard,
      closeWizard,
      nextStep,
      prevStep,
      goToStep,
      addEquipment,
      updateEquipment,
      removeEquipment,
      removePendingByTempIds,
      setEquipment,
      setPreexistingCategories,
      markCategorySaved,
      addSessionSavedEquipment,
      skipCategory,
      unskipCategory,
      resetWizard,
      isFirstStep,
      isLastStep,
      currentStepInfo,
      totalEquipmentCount,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Action functions are stable (useCallback with [])
    [state, isFirstStep, isLastStep, currentStepInfo, totalEquipmentCount]
  );

  return (
    <WizardContext.Provider value={value}>{children}</WizardContext.Provider>
  );
}

// Hook
export function useWizard(): WizardContextValue {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within WizardProvider');
  }
  return context;
}
